/**
 * Product Detail Page
 * Full product page with image, description, reviews, specs
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Main, Flex, Grid } from '../../../components/styled/Layout';
import { Card, CardContent } from '../../../components/styled/Card';
import { PrimaryButton, SecondaryButton } from '../../../components/styled/Button';
import { useAuth } from '../../../contexts/AuthContext';
import productService from '../services/productService';
import recentlyViewedService from '../services/recentlyViewedService';
import apiService from '../../../services/apiService';
import { useCart } from '../contexts/CartContext';
import useShopFeatures from '../hooks/useShopFeatures';
import RecentlyViewed from '../components/RecentlyViewed';
import { getRatingValue, getRatingCount, renderStars, formatPrice } from '../utils/productHelpers';
import './Shop.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariation, setSelectedVariation] = useState({});
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const { addItem: addToCart } = useCart();
  const features = useShopFeatures();

  useEffect(() => {
    loadProduct();
    if (features.reviews) {
      loadReviews();
    }
  }, [id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const data = await productService.getProduct(id);
      setProduct(data);
      
      // Track recently viewed
      if (data && features.recentlyViewed) {
        recentlyViewedService.addProduct(data);
      }
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReviews = async () => {
    try {
      const reviewData = await productService.getReviews(id);
      setReviews(reviewData || []);
    } catch (error) {
      console.error('Error loading reviews:', error);
    }
  };

  const handleAddToCart = () => {
    if (product && product.stockQty > 0) {
      addToCart(product, quantity, selectedVariation);
      // Show success message (could add toast notification)
      alert(`Added ${quantity} ${product.name} to cart!`);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!user) {
      alert('Please login to submit a review');
      navigate('/login?redirect=' + encodeURIComponent(location.pathname));
      return;
    }
    
    if (reviewText.trim().length < 10) {
      alert('Review must be at least 10 characters');
      return;
    }
    
    try {
      setSubmittingReview(true);
      await apiService.post('/shop/reviews', {
        productId: id,
        rating: reviewRating,
        reviewText: reviewText.trim()
      });
      
      alert('Review submitted successfully!');
      setShowReviewForm(false);
      setReviewText('');
      setReviewRating(5);
      
      // Reload reviews and product (to update rating)
      await Promise.all([
        loadReviews(),
        loadProduct()
      ]);
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Main>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading product...</p>
          </div>
        </Main>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container>
        <Main>
          <div className="empty-state">
            <h3>Product not found</h3>
            <p>The product you're looking for doesn't exist.</p>
            <SecondaryButton as={Link} to="/shop/catalog">
              Back to Catalog
            </SecondaryButton>
          </div>
        </Main>
      </Container>
    );
  }

  const specs = typeof product.specs === 'string' ? JSON.parse(product.specs) : (product.specs || {});
  const variations = typeof product.variations === 'string' ? JSON.parse(product.variations) : (product.variations || []);

  return (
    <Container>
      <Main>
        <div className="shop-page product-detail-page">
          {/* Breadcrumb */}
          <div className="breadcrumb">
            <Link to="/shop">Home</Link> / 
            <Link to="/shop/catalog">Products</Link> / 
            <span>{product.name}</span>
          </div>

          <Grid columns={2} gap="var(--space-5)" style={{ alignItems: 'start' }}>
            {/* Left: Image */}
            <div className="product-detail-image">
              <img src={product.image} alt={product.name} />
            </div>

            {/* Right: Product Info */}
            <div className="product-detail-info">
              <h1>{product.name}</h1>

              {/* Rating */}
              {product.rating && (
                <div className="product-rating">
                  <span className="stars">
                    {renderStars(product.rating)}
                  </span>
                  <span className="rating-value">
                    {getRatingValue(product.rating).toFixed(1)}
                  </span>
                  {getRatingCount(product.rating, product.ratingCount) > 0 && (
                    <span className="rating-count">
                      ({getRatingCount(product.rating, product.ratingCount)} reviews)
                    </span>
                  )}
                </div>
              )}

              {/* Price */}
              <div className="product-price-large">
                ${formatPrice(product.price)}
              </div>

              {/* Stock */}
              {features.stockIndicators && (
                <div className={`stock-badge ${product.stockQty > 0 ? 'in-stock' : 'out-of-stock'}`}>
                  {product.stockQty > 0 ? `In Stock (${product.stockQty} available)` : 'Out of Stock'}
                </div>
              )}

              {/* Description */}
              <div className="product-description">
                <h3>About this item</h3>
                <p>{product.description}</p>
              </div>

              {/* Variations */}
              {features.productVariations && variations.length > 0 && (
                <div className="product-variations">
                  {variations.map((variation, idx) => (
                    <div key={idx} className="variation-group">
                      <label>{variation.name}</label>
                      <div className="variation-options">
                        {variation.options.map(option => (
                          <button
                            key={option}
                            className={`variation-button ${selectedVariation[variation.name] === option ? 'selected' : ''}`}
                            onClick={() => setSelectedVariation({ ...selectedVariation, [variation.name]: option })}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Quantity */}
              {product.stockQty > 0 && (
                <div className="quantity-selector">
                  <label>Quantity:</label>
                  <Flex gap="8px" align="center">
                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>-</button>
                    <input 
                      type="number" 
                      value={quantity} 
                      onChange={(e) => setQuantity(Math.max(1, Math.min(product.stockQty, parseInt(e.target.value) || 1)))}
                      min="1"
                      max={product.stockQty}
                    />
                    <button onClick={() => setQuantity(Math.min(product.stockQty, quantity + 1))}>+</button>
                  </Flex>
                </div>
              )}

              {/* Add to Cart */}
              <Flex gap="12px" style={{ marginTop: '20px' }}>
                <PrimaryButton 
                  onClick={() => {
                    handleAddToCart();
                    if (product.stockQty > 0) {
                      alert(`✅ ${product.name} added to cart!\n\nClick the 🛒 cart icon in the top-right corner.`);
                    }
                  }}
                  disabled={product.stockQty === 0}
                  style={{ flex: 1 }}
                >
                  {product.stockQty > 0 ? 'Add to Cart' : 'Out of Stock'}
                </PrimaryButton>
                {features.wishlist && (
                  <SecondaryButton style={{ padding: '0 20px' }}>
                    ♡ Save
                  </SecondaryButton>
                )}
              </Flex>
            </div>
          </Grid>

          {/* Product Specifications */}
          {features.productSpecs && Object.keys(specs).length > 0 && (
            <Card style={{ marginTop: '32px' }}>
              <CardContent>
                <h2>Product Specifications</h2>
                <table className="specs-table">
                  <tbody>
                    {Object.entries(specs).map(([key, value]) => (
                      <tr key={key}>
                        <td className="spec-label">{key}</td>
                        <td className="spec-value">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Reviews Section */}
          {features.reviews && (
            <Card style={{ marginTop: '32px' }}>
              <CardContent>
                <Flex justify="space-between" align="center" style={{ marginBottom: '24px' }}>
                  <h2 style={{ margin: 0 }}>Customer Reviews</h2>
                  {user && !showReviewForm && (
                    <SecondaryButton onClick={() => setShowReviewForm(true)}>
                      ✍️ Write a Review
                    </SecondaryButton>
                  )}
                  {!user && (
                    <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
                      <Link to="/login" style={{ color: 'var(--color-primary)' }}>Login</Link> to write a review
                    </p>
                  )}
                </Flex>
                
                {/* Review Submission Form */}
                {showReviewForm && user && (
                  <Card style={{ marginBottom: '24px', backgroundColor: '#f9f9f9' }}>
                    <CardContent>
                      <h3>Write Your Review</h3>
                      <form onSubmit={handleSubmitReview}>
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            Rating
                          </label>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {[1, 2, 3, 4, 5].map(star => (
                              <button
                                key={star}
                                type="button"
                                onClick={() => setReviewRating(star)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  fontSize: '32px',
                                  cursor: 'pointer',
                                  color: star <= reviewRating ? '#FFA500' : '#ddd',
                                  padding: '0',
                                  transition: 'color 0.2s'
                                }}
                              >
                                ★
                              </button>
                            ))}
                            <span style={{ marginLeft: '12px', alignSelf: 'center', color: '#666' }}>
                              {reviewRating} star{reviewRating !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            Your Review
                          </label>
                          <textarea
                            value={reviewText}
                            onChange={(e) => setReviewText(e.target.value)}
                            placeholder="Share your experience with this product (minimum 10 characters)..."
                            rows="5"
                            style={{
                              width: '100%',
                              padding: '12px',
                              borderRadius: '4px',
                              border: '1px solid #ddd',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              resize: 'vertical'
                            }}
                            required
                          />
                          <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            {reviewText.length} characters (minimum 10)
                          </p>
                        </div>
                        
                        <Flex gap="var(--space-3)">
                          <PrimaryButton type="submit" disabled={submittingReview || reviewText.trim().length < 10}>
                            {submittingReview ? 'Submitting...' : 'Submit Review'}
                          </PrimaryButton>
                          <SecondaryButton 
                            type="button" 
                            onClick={() => {
                              setShowReviewForm(false);
                              setReviewText('');
                              setReviewRating(5);
                            }}
                          >
                            Cancel
                          </SecondaryButton>
                        </Flex>
                      </form>
                    </CardContent>
                  </Card>
                )}
                
                {/* Reviews List */}
                {reviews.length === 0 ? (
                  <p>No reviews yet. Be the first to review this product!</p>
                ) : (
                  <div className="reviews-list">
                    {reviews.map(review => (
                      <div key={review.id} className="review-item">
                        <Flex justify="space-between">
                          <div className="review-rating">
                            <span className="stars">
                              {'★'.repeat(review.rating || 0)}{'☆'.repeat(5 - (review.rating || 0))}
                            </span>
                            {review.verifiedPurchase && (
                              <span className="verified-badge">Verified Purchase</span>
                            )}
                          </div>
                          <div className="review-date">
                            {new Date(review.createdAt).toLocaleDateString()}
                          </div>
                        </Flex>
                        <p className="review-comment">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Recently Viewed Products */}
          <RecentlyViewed excludeProductId={id} />
        </div>
      </Main>
    </Container>
  );
};

export default ProductDetail;

